import React, { useMemo } from 'react';
import ErrorBoundary from '../ErrorBoundary';
import PanelErrorFallback from '../PanelErrorFallback';
import FibLevelsOverlay, { FibLevelsOverlayHandle } from '../FibLevelsOverlay';
import OrderLine, { OrderLineHandle } from '../OrderLine';
import LivePositionLine, { LivePositionLineHandle } from '../LivePositionLine';
import PositionControlsOverlay from '../PositionControlsOverlay';
import LiveOrderLine, { LiveOrderLineHandle } from '../LiveOrderLine';
import SignalPredictorOverlay from '../SignalPredictorOverlay';
import { BrokerPosition, BrokerOrder } from '../../store/brokerStore';
import { normalizeToBaseSymbol } from '../../store/settingsStore';
import { useLevelsStore } from '../../store/levelsStore';
import { useRestrainFor } from '../Restrain/useRestrain';
import { restrainLockMessage } from '../../store/restrainStore';
import { PricePaneClip } from './PricePaneClip';

export interface FibLevel {
  level: number;
  label: string;
  yPosition: number;
  price: number;
  isKey?: boolean;
}

export interface OrderLineData {
  id: string;
  price: number;
  yPosition: number;
}

interface ChartOverlaysProps {
  // Chart refs
  chartContainerRef: React.RefObject<HTMLDivElement>;
  candlestickSeriesRef: React.RefObject<any>;

  // Fib Levels
  fibLevels: FibLevel[];
  chartWidth: number;

  // Order Lines (paper trading)
  orderLines: OrderLineData[];
  currentPrice: number;
  onPlaceOrder: (type: 'BUY_LIMIT' | 'SELL_LIMIT' | 'BUY_STOP' | 'SELL_STOP', price: number, quantity: number) => void;
  onRemoveOrderLine: (id: string) => void;
  onDragOrderLine: (id: string, newYPosition: number) => void;

  // Trading panel settings for order lines
  useOCO?: boolean;
  ocoTPOffset?: number;
  ocoSLOffset?: number;

  // Live Position
  livePositionLineY: number | null;
  currentLivePosition: BrokerPosition | null;
  livePositionKey: string;
  onFlattenPosition: () => void;

  // Position Controls
  liveOrders: BrokerOrder[];
  selectedAccountId: number | null;
  symbol: string;
  onModifyOrder: (orderId: number, newPrice: number, isStopPrice: boolean, brokerId?: string) => void;
  onModifyOrderQuantity: (orderId: number, newQuantity: number) => void;
  onReplaceBracket: (params: {
    tpOrderId?: number;
    slOrderId?: number;
    symbol: string;
    action: 'Buy' | 'Sell';
    newQuantity: number;
    tpPrice?: number;
    slPrice?: number;
  }) => Promise<{ success: boolean; error?: string }>;
  onPartialExit: (exitQty: number) => void;
  /** Start a draggable PENDING take-profit bracket line for the active position. */
  onAddTP?: () => void;
  /** Start a draggable PENDING stop-loss bracket line for the active position. */
  onAddSL?: () => void;

  // Live Order Lines
  liveOrderLinePositions: Map<number, number>;
  liveOrderLabelOffsets?: Map<number, number>;
  coordinateToPrice: (yCoordinate: number) => number | null;
  onCancelOrder: (orderId: number, brokerId?: string) => void;
  onSplitOrder?: (order: any, qty1: number, qty2: number, price1: number, price2: number) => void;

  /** Whether to show trading overlays (position lines, order lines) */
  showTradingOverlays?: boolean;
  /** Whether to show position controls overlay (set to false in panel mode where it's rendered globally) */
  showPositionControls?: boolean;
  /** Whether to show signal predictor overlay (set to false in panel mode where it's rendered globally) */
  showSignalPredictor?: boolean;
  dcaMode?: boolean;
  onDcaToggle?: () => void;

  // Imperative refs for direct DOM position updates (bypass React re-renders during scroll)
  fibLevelsOverlayRef?: React.Ref<FibLevelsOverlayHandle>;
  livePositionLineRef?: React.Ref<LivePositionLineHandle>;
  orderLineRefsMap?: React.MutableRefObject<Map<string, OrderLineHandle>>;
  liveOrderLineRefsMap?: React.MutableRefObject<Map<number, LiveOrderLineHandle>>;
  /** Bottom of the price pane (px) while order-flow line panes are shown: lines are clipped there. */
  priceClipBottom?: number | null;
}

export const ChartOverlays: React.FC<ChartOverlaysProps> = ({
  fibLevels,
  chartWidth,
  orderLines,
  currentPrice,
  onPlaceOrder,
  onRemoveOrderLine,
  onDragOrderLine,
  useOCO = false,
  ocoTPOffset = 10,
  ocoSLOffset = 5,
  livePositionLineY,
  currentLivePosition,
  livePositionKey,
  onFlattenPosition,
  liveOrders,
  selectedAccountId,
  symbol,
  onModifyOrder,
  onModifyOrderQuantity,
  onReplaceBracket,
  onPartialExit,
  onAddTP,
  onAddSL,
  liveOrderLinePositions,
  liveOrderLabelOffsets,
  coordinateToPrice,
  onCancelOrder,
  onSplitOrder,
  showTradingOverlays = true,
  showPositionControls = true,
  showSignalPredictor = true,
  dcaMode = false,
  onDcaToggle,
  fibLevelsOverlayRef,
  livePositionLineRef,
  orderLineRefsMap,
  liveOrderLineRefsMap,
  priceClipBottom = null,
}) => {
  // Normalize chart symbol for matching
  const chartBaseSymbol = useMemo(() => normalizeToBaseSymbol(symbol), [symbol]);

  // Filter working live orders for rendering - only show orders matching chart symbol
  const workingLiveOrders = useMemo(() => {
    return liveOrders.filter(order => {
      // Filter by account if one is selected
      if (selectedAccountId !== null && order.accountId !== selectedAccountId) return false;
      // Must be a working order
      if (order.ordStatus !== 'Working' && order.ordStatus !== 'Accepted' && order.ordStatus !== 'Pending') return false;
      // Must have a price
      if (!order.stopPrice && !order.price) return false;
      // Filter by symbol - only show orders for current chart's symbol
      if (order.symbol && chartBaseSymbol) {
        const orderBaseSymbol = normalizeToBaseSymbol(order.symbol);
        if (orderBaseSymbol !== chartBaseSymbol) return false;
      }
      return true;
    });
  }, [liveOrders, selectedAccountId, chartBaseSymbol]);

  const fibVisible = useLevelsStore((s) => s.visibility.fibonacci);

  // Restrain on the chart's live position: click-to-add order lines grey out
  // the buttons that would change its size.
  const { entry: restrain } = useRestrainFor(
    currentLivePosition ? { ...currentLivePosition, fallbackSymbol: symbol } : null,
  );
  const restrainMessage = restrain ? restrainLockMessage(restrain) : null;

  return (
    <>
      {/* Price-anchored lines: clipped to the price pane when flow line panes are up. */}
      <PricePaneClip bottom={priceClipBottom}>
      {/* Fibonacci Levels Overlay */}
      {fibLevels.length > 0 && fibVisible && (
        <ErrorBoundary
          name="FibLevelsOverlay"
          fallback={<PanelErrorFallback error={null} onRetry={() => window.location.reload()} panelName="Fib Levels" />}
        >
          <FibLevelsOverlay
            ref={fibLevelsOverlayRef}
            levels={fibLevels}
            chartWidth={chartWidth}
          />
        </ErrorBoundary>
      )}

      {/* Order Lines (Paper Trading) */}
      {orderLines.map(line => (
        <OrderLine
          key={line.id}
          ref={(handle: OrderLineHandle | null) => {
            if (handle && orderLineRefsMap) orderLineRefsMap.current.set(line.id, handle);
            else if (orderLineRefsMap) orderLineRefsMap.current.delete(line.id);
          }}
          id={line.id}
          price={line.price}
          yPosition={line.yPosition}
          currentPrice={currentPrice}
          onPlaceOrder={onPlaceOrder}
          onRemove={onRemoveOrderLine}
          onDrag={onDragOrderLine}
          useOCO={useOCO}
          ocoTPOffset={ocoTPOffset}
          ocoSLOffset={ocoSLOffset}
          restrainNetPos={currentLivePosition?.netPos ?? 0}
          restrainMessage={restrainMessage}
        />
      ))}

      {/* Trading overlays - only show on active panel in grid mode */}
      {showTradingOverlays && (
        <>
          {/* Live Position Line - shows Tradovate position on chart */}
          {livePositionLineY !== null && currentLivePosition && (
            <LivePositionLine
              ref={livePositionLineRef}
              key={livePositionKey}
              position={currentLivePosition}
              yPosition={livePositionLineY}
              currentPrice={currentPrice}
              onFlatten={onFlattenPosition}
              symbol={symbol}
              orders={liveOrders}
            />
          )}

          {/* Live Order Lines - draggable Tradovate resting orders (SL/TP) */}
          {workingLiveOrders.map((order, index) => {
            const yPosition = liveOrderLinePositions.get(order.id);
            if (yPosition === undefined) return null;
            return (
              <LiveOrderLine
                key={order.id}
                ref={(handle: LiveOrderLineHandle | null) => {
                  if (handle && liveOrderLineRefsMap) liveOrderLineRefsMap.current.set(order.id, handle);
                  else if (liveOrderLineRefsMap) liveOrderLineRefsMap.current.delete(order.id);
                }}
                order={order}
                yPosition={yPosition}
                labelOffset={liveOrderLabelOffsets?.get(order.id) || 0}
                horizontalOffset={index * 10}
                position={currentLivePosition}
                onCoordinateToPrice={coordinateToPrice}
                onModifyOrder={onModifyOrder}
                onCancelOrder={onCancelOrder}
                onSplitOrder={onSplitOrder}
                paneBottom={priceClipBottom}
              />
            );
          })}
        </>
      )}
      </PricePaneClip>

      {showTradingOverlays && (
        <>
          {/* Position Controls Overlay - draggable panel with action buttons */}
          {showPositionControls && currentLivePosition && currentLivePosition.netPos !== 0 && (
            <ErrorBoundary
              name="PositionControlsOverlay"
              fallback={<PanelErrorFallback error={null} onRetry={() => window.location.reload()} panelName="Position Controls" />}
            >
              <PositionControlsOverlay
                position={currentLivePosition}
                currentPrice={currentPrice}
                orders={liveOrders.filter(order =>
                  (selectedAccountId === null || order.accountId === selectedAccountId) &&
                  (order.ordStatus === 'Working' || order.ordStatus === 'Accepted' || order.ordStatus === 'Pending')
                )}
                symbol={symbol}
                onFlatten={onFlattenPosition}
                onModifyOrder={onModifyOrder}
                onModifyOrderQuantity={onModifyOrderQuantity}
                onReplaceBracket={onReplaceBracket}
                onPartialExit={onPartialExit}
                dcaMode={dcaMode}
                onDcaToggle={onDcaToggle}
                onAddTP={onAddTP}
                onAddSL={onAddSL}
              />
            </ErrorBoundary>
          )}

        </>
      )}

      {/* Signal Predictor Overlay - DEPRECATED (use Android widget instead) */}
      {false && showSignalPredictor && (
        <ErrorBoundary
          name="SignalPredictorOverlay"
          fallback={<PanelErrorFallback error={null} onRetry={() => window.location.reload()} panelName="Signal Predictor" />}
        >
          <SignalPredictorOverlay symbol={symbol} embedded />
        </ErrorBoundary>
      )}
    </>
  );
};
